import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import apiLink from "../../../api/apiLink";
import Loader from "../../../components/common/Loader";
import { smsCount } from "../../../components/common/UtilityMethods";
import { smsSettingUpdateIsp } from "../../../features/authSlice";
import { audioFileUpload } from "../../../features/apiCallAdmin";
import useISPowner from "../../../hooks/useISPOwner";
import AudioUploadPlayer from "./AudioUploadPlayer";

/* -------------------------------------Constants------------------------------------- */

const SMS_LIMIT = 335;

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => {
  const value = String(i + 1);
  return { value, label: value };
});

const STATUS_OPTIONS = ["active", "inactive", "expired", "paid", "unpaid"];

const field = (id, name, value, label = value) => ({ id, name, value, label });

const BASE_FIELDS = [
  field("customerUserName", "user_name", "USER: USERNAME"),
  field("customerUserId", "customer_id", "ID: CUSTOMER_ID"),
  field("customerName", "customer_name", "NAME: CUSTOMER_NAME"),
  field("customerBillAmount", "amount", "BILL: AMOUNT"),
  field("customerBillData", "bill_date", "LAST DATE: BILL_DATE"),
];

const PAYMENT_LINK_ID = "PAYMENT_LINK_ALERT";
const PAYMENT_LINK_PREFIX = "Payment Link: https://app.one-billing.com/isp/";
const PAYMENT_LINK_REGEX =
  /Payment Link: https:\/\/app\.one-billing\.com\/isp\/\S*/;

/* --------------------------------------helpers------------------------------------ */

const toggleInList = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const toDayOptions = (days = []) => {
  const saved = days.map(String);
  return DAY_OPTIONS.filter((d) => saved.includes(d.value));
};

const buildTemplateFields = ({ hasPG, netFeeId }) => [
  ...BASE_FIELDS,
  ...(hasPG && netFeeId
    ? [
        field(
          PAYMENT_LINK_ID,
          "payment_link",
          `${PAYMENT_LINK_PREFIX}${netFeeId}`,
          "PAYMENT_LINK",
        ),
      ]
    : []),
];

const parseTemplate = (saved = "") => {
  const selectedIds = BASE_FIELDS.filter((f) => saved.includes(f.value)).map(
    (f) => f.id,
  );
  if (PAYMENT_LINK_REGEX.test(saved)) selectedIds.push(PAYMENT_LINK_ID);

  const stripped = BASE_FIELDS.reduce(
    (text, f) => text.replace(f.value, ""),
    saved.replace(PAYMENT_LINK_REGEX, ""),
  );
  const [title = "", ...rest] = stripped.split("\n");

  return {
    title,
    bottomText: rest.join("\n").replace(/^\n+/, ""),
    selectedIds,
  };
};

const readModeSettings = (sms, type) =>
  type === "voice"
    ? {
        days: sms?.voiceCalenderAlertDays,
        status: sms?.voiceTemplate?.calenderAlertCustomerStatus,
      }
    : {
        days: sms?.calenderDays,
        status: sms?.template?.calenderAlertCustomerStatus,
      };

const buildModePatch = (sms, type, { days, status, message, voice }) =>
  type === "voice"
    ? {
        voiceCalenderAlertDays: days,
        voiceTemplate: {
          ...sms?.voiceTemplate,
          ...(voice && { calenderAlert: voice }),
          calenderAlertCustomerStatus: status,
        },
      }
    : {
        calenderDays: days,
        template: {
          ...sms?.template,
          calenderAlertCustomerStatus: status,
          calenderAlert: message,
        },
      };

const CalenderAlert = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ispOwnerData, ispOwnerId, settings, bpSettings } = useISPowner();

  const sms = settings?.sms;
  const savedAudio = sms?.voiceTemplate?.calenderAlert;

  /* ------------------------------- State ---------------------------------- */

  const [loading, setLoading] = useState(false);

  // general
  const [messageType, setMessageType] = useState("text");
  const [sendingType, setSendingType] = useState();
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [calenderDays, setCalenderDays] = useState([]);
  const [status, setStatus] = useState([]);

  // text message
  const [title, setTitle] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bottomText, setBottomText] = useState("");

  // voice message
  const [audioFile, setAudioFile] = useState();

  useEffect(() => {
    if (!sms) return;

    const type = sms.calenderAlertMessageType || "text";
    const modeSettings = readModeSettings(sms, type);
    const parsed = parseTemplate(sms.template?.calenderAlert);

    setMessageType(type);
    setSendingType(sms.calenderAlertSendBy);
    setAlertEnabled(Boolean(sms.calenderAlert));
    setCalenderDays(toDayOptions(modeSettings.days));
    setStatus(modeSettings.status ?? []);

    setTitle(parsed.title);
    setBottomText(parsed.bottomText);
    setSelectedIds(parsed.selectedIds);
  }, [sms]);

  /* ----------------------------- Derived data ----------------------------- */

  const templateFields = useMemo(
    () =>
      buildTemplateFields({
        hasPG: ispOwnerData?.bpSettings?.hasPG,
        netFeeId: ispOwnerData?.netFeeId,
      }),
    [ispOwnerData?.bpSettings?.hasPG, ispOwnerData?.netFeeId],
  );

  const activeFields = templateFields
    .filter((f) => selectedIds.includes(f.id))
    .map((f) => f.value);

  const message = [title, ...activeFields, bottomText].join("\n");
  const headerLength = [title, ...activeFields].join("\n").length + 1;
  const allStatusSelected = status.length === STATUS_OPTIONS.length;

  const messageTypes = useMemo(
    () => [
      { label: t("text"), value: "text" },
      ...(bpSettings?.voiceMessage
        ? [{ label: t("voice"), value: "voice" }]
        : []),
    ],
    [t, bpSettings?.voiceMessage],
  );

  const sendingMessageTypes = useMemo(
    () => [
      { label: t("nonMasking"), value: "nonMasking" },
      { label: t("masking"), value: "masking" },
      { label: t("fixedNumber"), value: "fixedNumber" },
      ...(bpSettings?.hasSmsGateway
        ? [{ label: t("personalGateway"), value: "personalGateway" }]
        : []),
    ],
    [t, bpSettings?.hasSmsGateway],
  );

  const onOffOptions = [
    { label: t("ON"), value: "on" },
    { label: t("OFF"), value: "off" },
  ];

  /* ------------------------------ Handlers -------------------------------- */

  const handleMessageTypeChange = (type) => {
    const modeSettings = readModeSettings(sms, type);
    setMessageType(type);
    setCalenderDays(toDayOptions(modeSettings.days));
    setStatus(modeSettings.status ?? []);
  };

  const toggleField = (f) => {
    const isAdding = !selectedIds.includes(f.id);
    if (isAdding && message.length + f.value.length + 1 > SMS_LIMIT) {
      toast.error(t("exceedSMSLimit"));
      return;
    }
    setSelectedIds((prev) => toggleInList(prev, f.id));
  };

  const uploadVoice = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    if (savedAudio?.publicID) formData.append("publicId", savedAudio.publicID);

    const uploaded = await audioFileUpload(setLoading, formData);
    setLoading(true);

    const f = uploaded?.[0];
    if (!f) {
      toast.error("Voice file upload failed.");
      return null;
    }
    return {
      originalName: f.originalName,
      path: f.path,
      publicID: f.publicID,
      duration: f.duration,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isVoice = messageType === "voice";

    if (isVoice && !audioFile && !savedAudio) {
      toast.error("Voice file is required.");
      return;
    }

    setLoading(true);
    try {
      let voice;
      if (isVoice && audioFile) {
        voice = await uploadVoice(audioFile);
        if (!voice) return;
      }

      const payload = {
        ...sms,
        calenderAlertMessageType: messageType,
        calenderAlertSendBy: sendingType,
        calenderAlert: alertEnabled,
        ...buildModePatch(sms, messageType, {
          days: calenderDays.map((d) => d.value),
          status,
          message,
          voice,
        }),
      };

      const res = await apiLink.patch(
        `/ispOwner/settings/sms/${ispOwnerId}`,
        payload,
      );
      dispatch(smsSettingUpdateIsp(res.data));
      setAudioFile(undefined);
      toast.success(t("alertSMStemplateSaveAlert"));
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error updating SMS settings",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ------------- Small render helpers ------------------------ */

  const renderRadios = (name, options, value, onChange) =>
    options.map((o) => {
      const id = `calender-${name}-${o.value}`;
      return (
        <div className="message_radio" key={o.value}>
          <input
            type="radio"
            id={id}
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={(e) => onChange(e.target.value)}
          />
          <label htmlFor={id}>{o.label}</label>
        </div>
      );
    });

  const renderCheckbox = ({ id, name, value, label, checked, onChange }) => (
    <div className="radioselect" key={id}>
      <input
        id={id}
        type="checkbox"
        className="getValueUsingClass"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <label className="templatelabel" htmlFor={id}>
        {label}
      </label>
    </div>
  );

  return (
    <div>
      <form onSubmit={handleSubmit} className="settingForm">
        <div className="writeMessageSection displayGrid">
          <div className="messageStatus d-flex justify-content-between">
            <div className="message-sending-type">
              <h5>{t("messageType")}</h5>
              <div className="smsType">
                {renderRadios(
                  "messageType",
                  messageTypes,
                  messageType,
                  handleMessageTypeChange,
                )}
              </div>
            </div>

            <div className="sending-status">
              <h4>{t("calenderAlertTemplate")}</h4>
              <div className="displayGrid1">
                {renderRadios(
                  "billConfirmation",
                  onOffOptions,
                  alertEnabled ? "on" : "off",
                  (v) => setAlertEnabled(v === "on"),
                )}
              </div>
            </div>

            {messageType === "text" && (
              <div className="message-sending-type">
                <h4>{t("sendingMessageType")}</h4>
                <div className="smsType">
                  {renderRadios(
                    "sendingType",
                    sendingMessageTypes,
                    sendingType,
                    setSendingType,
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="templateSelect">
            <p>{t("selectMonthDate")}</p>
            <Select
              value={calenderDays}
              onChange={(value) => setCalenderDays([...value])}
              isMulti
              name="day"
              options={DAY_OPTIONS}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder={t("select")}
            />
          </div>

          <div className="templateSelect">
            <p>{t("customerStatus")}</p>
            <div className="d-flex flex-wrap gap-3">
              {STATUS_OPTIONS.map((s) =>
                renderCheckbox({
                  id: `${s}Customer`,
                  name: s,
                  value: s,
                  label: t(s),
                  checked: status.includes(s),
                  onChange: () => setStatus((prev) => toggleInList(prev, s)),
                }),
              )}
              {renderCheckbox({
                id: "selectAllCustomerStatus",
                name: "selectAll",
                value: "selectAll",
                label: t("selectAll"),
                checked: allStatusSelected,
                onChange: () =>
                  setStatus(allStatusSelected ? [] : [...STATUS_OPTIONS]),
              })}
            </div>
          </div>

          {messageType === "text" ? (
            <>
              <div className="w-25">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-control"
                  type="text"
                  placeholder={t("title")}
                  maxLength={40}
                />
              </div>

              <div className="billconfirm">
                <div className="showthesequence">
                  <p className="endingText">{title}</p>
                  {activeFields.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  <p className="endingtext">{bottomText}</p>
                </div>

                <div className="displayFlexx">
                  <div className="displayFlexx me-4">
                    {templateFields.map((f) =>
                      renderCheckbox({
                        ...f,
                        checked: selectedIds.includes(f.id),
                        onChange: () => toggleField(f),
                      }),
                    )}
                  </div>
                </div>
              </div>

              <div className="smsCount">
                <span className="smsLength">
                  {t("letter")}
                  {message.length}
                </span>
                <span>SMS: {smsCount(message)}</span>
              </div>

              <textarea
                id="messageTextArea"
                rows="6"
                className="form-control"
                placeholder={t("messageLikhun")}
                value={bottomText}
                maxLength={Math.max(0, SMS_LIMIT - headerLength)}
                onChange={(e) => setBottomText(e.target.value)}
              />
            </>
          ) : (
            <AudioUploadPlayer
              file={audioFile}
              savedAudio={savedAudio}
              onFileChange={setAudioFile}
            />
          )}
        </div>
        <hr />
        <footer>
          <button type="submit" className="btn btn-success">
            {loading ? <Loader /> : t("save")}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default CalenderAlert;
